/*
 * Module for the response editor
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "messaging/messenger",
  "modules/shared/ckeditor5/ckEditor5Module",
  "modules/shared/ckeditor5/cloud/cloudModel",
  "modules/topicsSplit/topic/topicResponseEditorModal/topicResponseEditorModalView",
  "modules/shared/ckeditor5/baseModels/collaborationFeaturesModel",
  "modules/topicsSplit/topic/topicResponseEditorModal/topicResponseEditorModalModel",
], function (
  Marionette,
  Backbone,
  $,
  Messenger,
  Es6CkEditor5Module,
  CkEditorCloudModel,
  TopicResponseEditorModalView,
) {
  "use strict";

  const CkEditor5Module = Es6CkEditor5Module.default || Es6CkEditor5Module;

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "topicResponseEditorModal";
      this._setUpReqRes();
      this._setUpVents();
    },
    show: function (conversationObject) {
      this.conversationNode = conversationObject.node;
      this.selectedTouchPoint = conversationObject.selectedTouchPoint;
      this.selectedLanguage = conversationObject.selectedLanguage;
      this.selectedResponseId = conversationObject.node.get("responseId");
      this.responseTemplate = this.conversationNode.findOrAddResponseTemplate(
        this.selectedTouchPoint,
        this.selectedLanguage,
      );

      const response = new Backbone.Model({
        response: "",
        touchPointId: this.selectedTouchPoint,
        languageId: this.selectedLanguage,
        responseId: this.selectedResponseId,
      });
      const responseText = this.responseTemplate.get("response");
      //add data-rdprivileges-name attribute to any privileged areas
      response.set(
        "response",
        this.conversationNode.addPrivilegedRoleNamesToResponse(responseText),
      );
      this.view = new TopicResponseEditorModalView.topicResponseEditorModalView({
        model: response,
      });
      this.view.on("done", this._saveResponse, this);
      this.view.on("cancel", this._discardResponse, this);

      this.htmlEditor = this.addSubModule(
        new CkEditor5Module.ckEditor5Module({ namespace: this.name, region: this.view.htmlEditor }),
      );
      this.htmlEditor.on("editor:initialized", () => {
        this.view.enableDoneButton();
        this.view.enableCancelButton();
      });
      let skipCollaborationFeatures = !!conversationObject.skipCollaborationFeatures;
      this.skipCollaborationFeatures = skipCollaborationFeatures;
      if (!skipCollaborationFeatures) {
        this.documentId = this.responseTemplate.get("templateId");
        if (!this.documentId) {
          console.error("Response template does not have an ID, collaboration is disabled");
          skipCollaborationFeatures = true;
        } else if (typeof this.documentId != "string") {
          this.documentId = this.documentId.toString();
        }
      }
      let allowAiFeatures = !!conversationObject.allowAiFeatures;
      let skipExpandWithContext = !!conversationObject.skipExpandWithContext;
      let skipResponseTransform = !!conversationObject.skipResponseTransform;
      let allowEditSource = !!conversationObject.allowEditSource;

      const commentThreads = skipCollaborationFeatures
        ? null
        : this.responseTemplate.commentThreads();

      // Note: When using editor bundle and importing document into CK cloud, we need to provide
      // the same version number provided by Ck cloud Export API endpoint. Providing any other
      // number here will result in revision history not getting updated as user types into
      // response editor and there may not be any error.
      const ckCloudDocumentVersion =
        this.responseTemplate.get("ckCloudDocumentVersion") > 0
          ? this.responseTemplate.get("ckCloudDocumentVersion")
          : 0;

      this.view.on("show", () => {
        this.htmlEditor.show({
          languageId: response.get("languageId"),
          touchPointId: response.get("touchPointId"),
          responseId: response.get("responseId"),
          response: response.get("response"),
          allCategories: Messenger.reqres.request("kb:categories"),
          attributes: this.conversationNode.topicAttributes(),
          categories: this.conversationNode.categories(),
          commentThreads: commentThreads,
          documentId: this.documentId,
          version: this.responseTemplate.get("version"),
          ckCloudDocumentVersion: ckCloudDocumentVersion,
          skipCollaborationFeatures: skipCollaborationFeatures,
          allowAiFeatures: allowAiFeatures,
          skipExpandWithContext: skipExpandWithContext,
          skipResponseTransform: skipResponseTransform,
          allowEditSource: allowEditSource,
        });
      });

      const downloadCookie = Messenger.reqres.request("get:download:cookie");

      this.showRegion(this.view, { loading: true, entity: downloadCookie });
      this.view.disableDoneButton();
      this.view.disableCancelButton();
    },
    _discardResponse: function () {
      Messenger.vent.trigger("show:saving:display");
      if (this.htmlEditor.isCollaborationEnabled()) {
        this._deleteCkCloudDocumentIfLastUserAndCloseModal();
      } else {
        this._destroyTemplateIfNew();
        Messenger.vent.trigger("close:modal");
        Messenger.vent.trigger("close:saving:display");
      }
    },
    _deleteCkCloudDocumentIfLastUserAndCloseModal: function () {
      this.htmlEditor
        .getSessionConnectedUsers(this.documentId, this.selectedResponseId)
        .then((connectedUsers) => {
          if (!connectedUsers || connectedUsers.length <= 1) {
            Messenger.yesNoDialog(
              $.t("topic.response.lastUserDiscardWarning"),
              {
                yes: () => {
                  this._destroyTemplateIfNew();
                  this._deleteCkEditorCloudDocument();
                  Messenger.vent.trigger("close:modal");
                },
              },
              this,
            );
          } else {
            Messenger.yesNoDialog(
              $.t("topic.response.multipleUsersDiscardWarning"),
              {
                yes: () => {
                  this._destroyTemplateIfNew();
                  Messenger.vent.trigger("close:modal");
                },
              },
              this,
            );
          }
        })
        .catch((error) => Messenger.okDialog(error))
        .finally(() => {
          Messenger.vent.trigger("close:saving:display");
        });
    },
    _destroyTemplateIfNew: function () {
      if (this.responseTemplate.isNew()) {
        // A new response template was just added to allow editing. Since editing was cancelled, remove it.
        this.responseTemplate.destroy();
      }
    },
    _updateResponse: function (conversationNode, selectedTouchPoint, selectedLanguage) {
      return new Promise((resolve, reject) => {
        // Check if the HTML Editor is dirty, to avoid saving a response that
        // essentially the same to the database even though there were no real changes when
        // collaboration is turned off.
        // Unfortunately if real-time collaboration is turned on, this technique no longer
        // works, especially if this user is the last to click Done, but didn't actually make any
        // changes. If we don't always update the response, then changes by any user can be lost.
        if (!this.skipCollaborationFeatures || this.htmlEditor.isDirty()) {
          let newResponse = this.htmlEditor.getResponse();
          //remove any data-rdprivileges-name attributes from the response
          newResponse = conversationNode.removePrivilegedRoleNamesFromResponse(newResponse);
          newResponse = conversationNode.removeImageDataWidgetFromResponse(newResponse);
          //update the conversationNode with the new response
          const responseTemplate = conversationNode.setResponse(
            selectedTouchPoint,
            selectedLanguage,
            newResponse,
          );

          if (responseTemplate) {
            responseTemplate.set("version", this.htmlEditor.getResponseVersion());
            responseTemplate.saveModel({
              error: (_model, response) => reject(Messenger.getErrorFromResponse(response)),
              success: () => resolve(),
            });
          } else {
            // This should never happen because setResponse should always create a Response Template
            // if it doesn't already exist
            reject("Failed to update response");
          }
        }
        resolve();
      });
    },
    _deleteCkEditorCloudDocument: function () {
      const ckEditorTokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
      const documentId = this.documentId;
      if (!ckEditorTokenUrl || !documentId) {
        return Promise.resolve();
      }
      const responseId = this.selectedResponseId;
      return new Promise((resolve, reject) => {
        // delete the response template document in CkEditor cloud
        const topicId = Messenger.reqres.request("current:topic:id");
        const ckEditorCloudDocumentModel = new CkEditorCloudModel.removeDocument({
          intentId: topicId,
          documentId: documentId,
          responseId: responseId,
        });
        ckEditorCloudDocumentModel.destroy({
          success: () => {
            console.log("CK cloud document " + documentId + " deleted");
            resolve();
          },
          error: (_model, response) => {
            console.log("Could not delete CK cloud document " + documentId);
            reject(Messenger.getErrorFromResponse(response));
          },
        });
      });
    },
    _exportCkEditorCloudDocument: function (documentId, responseId, respTempId, ckDocumentVersion) {
      const ckEditorTokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
      if (!ckEditorTokenUrl || !documentId) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const topicId = Messenger.reqres.request("current:topic:id");
        let docVersion;
        if (ckDocumentVersion && ckDocumentVersion > 0) {
          docVersion = ckDocumentVersion;
        } else {
          docVersion = 0;
        }
        const ckEditorCloudDocumentModel = new CkEditorCloudModel.exportDocument({
          intentId: topicId,
          documentId: documentId,
          responseId: responseId,
        });
        let newCkCloudDocumentVersion = 0;
        if (
          this.htmlEditor.getCloudDocumentVersion() &&
          this.htmlEditor.getCloudDocumentVersion() > 0
        ) {
          newCkCloudDocumentVersion = this.htmlEditor.getCloudDocumentVersion();
        }
        ckEditorCloudDocumentModel.set("respTempId", respTempId);
        ckEditorCloudDocumentModel.set("id", documentId);
        ckEditorCloudDocumentModel.set(
          "content",
          this._prepareCkDocumentContentForExport(docVersion),
        );
        ckEditorCloudDocumentModel.set("newCkCloudDocumentVersion", newCkCloudDocumentVersion);
        ckEditorCloudDocumentModel.saveModel({
          success: () => resolve(ckEditorCloudDocumentModel),
          error: (_model, response) => reject(Messenger.getErrorFromResponse(response)),
        });
      });
    },

    _prepareCkDocumentContentForExport: function (version) {
      return {
        data: "", // Note: data is not currently used by the server and should not be used as this is an export call to get data from CK cloud.
        version: version,
      };
    },

    _saveResponse: async function () {
      let allowClose = true;
      let savingDisplayShown = false;
      try {
        // check the number of connect users in this session. If this is the only user, then save
        // the response, otherwise show a warning message and wait for the last user to save changes.
        const connectedUsers = await this.htmlEditor.getSessionConnectedUsers(
          this.documentId,
          this.selectedResponseId,
        );
        if (connectedUsers && connectedUsers.length > 1) {
          // Note: response template may or may not have changed. It is possible a user only
          // changed comments, suggestions, or revisions. In this case, we still need to warn the
          // users that the changes can only be saved by the last user.
          const buttonPressed = await Messenger.yesNoDialogAsync(
            $.t("topic.response.multipleUsersEditingWarning"),
          );
          if (buttonPressed == Messenger.BUTTONS.YES) {
            this._destroyTemplateIfNew();
          } else {
            allowClose = false;
          }
        } else {
          // disable done button to prevent user from submitting the same request twice
          this.view.disableDoneButton();
          Messenger.vent.trigger("show:saving:display");
          savingDisplayShown = true;
          const kbId = Messenger.reqres.request("active:kb");
          const intentId = Messenger.reqres.request("current:topic:id");
          const isContentEmbeddedValid = await this.conversationNode.validateEmbeddedContent(
            this.selectedTouchPoint,
            this.selectedLanguage,
            this.htmlEditor.getResponse(),
            kbId,
            intentId,
          );
          if (!isContentEmbeddedValid.get("isSafe")) {
            allowClose = false;
            // noinspection ExceptionCaughtLocallyJS
            throw new Error(isContentEmbeddedValid.get("reason"));
          }
          // update the response
          await this._updateResponse(
            this.conversationNode,
            this.selectedTouchPoint,
            this.selectedLanguage,
          );

          let deleteCkCloudDoc = this.htmlEditor.isCollaborationEnabled();
          if (this.htmlEditor.isDirty() && allowClose) {
            await this._exportCkEditorCloudDocumentAndSetTheUpdatedTemplateVersion();
          } else if (this.htmlEditor.isCollaborationEnabled() && allowClose) {
            // response text was not modified, but comments, suggestions, or revisions may
            // have been modified. We do not have a way to check if any of this was changed
            // since we are not changing our models when Ck Cloud is adding or updating any of these.
            const responseTemplate = this.conversationNode.findResponseTemplate(
              this.selectedTouchPoint,
              this.selectedLanguage,
            );
            if (responseTemplate) {
              await this._exportCkEditorCloudDocumentAndSetTheUpdatedTemplateVersion();
            } else {
              deleteCkCloudDoc = false;
            }
          }

          if (deleteCkCloudDoc) {
            await this._deleteCkEditorCloudDocument();
          }
        }
      } catch (error) {
        console.error("Could not update response template", error);
        Messenger.okDialog(error);
      } finally {
        if (savingDisplayShown) {
          Messenger.vent.trigger("close:saving:display");
        }
        // Fire event that the node was selected in order to rerender the response preview
        if (allowClose) {
          Messenger.vent.trigger("selected:node", this.conversationNode);
          Messenger.vent.trigger("close:modal");
        } else {
          this.view.enableDoneButton();
        }
      }
    },

    _exportCkEditorCloudDocumentAndSetTheUpdatedTemplateVersion: function () {
      return this._exportCkEditorCloudDocument(
        this.documentId,
        this.selectedResponseId,
        this.responseTemplate.get("templateId"),
        this.responseTemplate.get("ckCloudDocumentVersion"),
      ).then((model) => {
        if (model) {
          this.responseTemplate.set("version", model.get("respTempVersion"));
          this.responseTemplate.set(
            "ckCloudDocumentVersion",
            model.get("newCkCloudDocumentVersion"),
          );
        }
      });
    },
    _setUpReqRes: function () {
      Messenger.reqres.setHandler(
        "ckeditor:editSnippet",
        async (snippetId) => {
          await this._saveResponse();
          await Messenger.savePromise();
          Messenger.vent.trigger("show:snippet:edit:page", snippetId);
        },
        this,
      );
    },
    _setUpVents: function () {
      Messenger.vent.on(
        Messenger.getFullName("ckeditor:deleteDocument", this.name),
        () => this._deleteCkEditorCloudDocument(),
        this,
      );
      Messenger.vent.on(
        Messenger.getFullName("ckeditor:initializeError", this.name),
        () => Messenger.vent.trigger("close:modal"),
        this,
      );
    },
  });
});
